import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Box, Icon, Typography } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { getMessageKey } from '../../../../../utils/utils';
import LinearProgressBar from '../../../../Common/LinearProgressBar';

import {
  DECISION_ICONS,
  DECISION_TITLES,
  TYPE_ICONS,
  TYPE_TITLES_AND_ORDER,
} from './constant';

import './CertsListSidePanelBodyCommon.css';

const CARD_TYPE = {
  decision: 1,
  type: 2,
};

function ProgressCard({ cardType, action, title, count, totalCount }) {
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const TYPE_TITLES = {
    Role:
      messages['Role_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Role_DISPLAYNAME.CERTIFICATIONS')),
    Account:
      messages['Account_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Account_DISPLAYNAME.CERTIFICATIONS')),
    User:
      messages['User_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('User_DISPLAYNAME.CERTIFICATIONS')),
    Entitlement:
      messages['Ent_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Ent_DISPLAYNAME.CERTIFICATIONS')),
    ParentEnt:
      messages['Ent_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('Ent_DISPLAYNAME.CERTIFICATIONS')),
    ChildEntitlement:
      messages['ChildEnt_DISPLAYNAME.CERTIFICATIONS'] ||
      localize(intl, getMessageKey('ChildEnt_DISPLAYNAME.CERTIFICATIONS')),
    allitems:
      messages.CERTS_EXPORT_WHAT_TO_EXPORT_ALL_ITEMS ||
      localize(intl, getMessageKey('CERTS_EXPORT_WHAT_TO_EXPORT_ALL_ITEMS')), //
    default:
      messages.CERTS_NOT_PROVIDED ||
      localize(intl, getMessageKey('CERTS_NOT_PROVIDED')),
  };

  if (cardType === CARD_TYPE.decision) {
    const decisionClasses = classnames(
      'ProgressCard-decision',
      !action.description && 'ProgressCard-decision--isCentered'
    );

    return (
      <Box tag='div' className={decisionClasses}>
        <Typography
          kind='body1'
          desktop='body1-d'
          mobile='body1-d'
          color='--color-foreground-secondary-bold'
          className='ProgressCard-count'>
          {count}
        </Typography>
        <Icon
          size='medium'
          kind={DECISION_ICONS[action.title] || DECISION_ICONS.Done}
          color='navy-900'
        />
        {action.description ? (
          <Box tag='div'>
            <Typography
              kind='body2'
              desktop='body2-d'
              mobile='body2-d'
              color='--color-foreground-secondary-bold'>
              {action.title}
            </Typography>
            <Typography
              kind='body3'
              desktop='body3-d'
              mobile='body3-d'
              color='--color-foreground-neutral-subtle'>
              {action.description}
            </Typography>
          </Box>
        ) : (
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color='--color-foreground-secondary-bold'>
            {action.title}
          </Typography>
        )}
      </Box>
    );
  }

  if (cardType === CARD_TYPE.type) {
    return (
      <Box tag='div' className='ProgressCard-type'>
        {Object.keys(TYPE_ICONS).includes(title) && (
          <div className='ProgressCard-icon'>
            <Icon size='small' kind={TYPE_ICONS[title]} color='navy-900' />
          </div>
        )}
        <Box tag='div' className='ProgressCard-content'>
          <Typography
            kind='body2-bold'
            desktop='body2-bold-d'
            mobile='body2-bold-d'
            color='--color-foreground-secondary-bold'>
            {TYPE_TITLES[title] || TYPE_TITLES.default}
          </Typography>
          <LinearProgressBar
            value={
              totalCount !== 0 ? Math.round((count * 100) / totalCount) : 0
            }
          />
          <Box tag='div' className='ProgressCard-contentStats'>
            <Typography
              kind='body3'
              desktop='body3-d'
              mobile='body3-d'
              color='--color-foreground-secondary-bold'>
              {count}
            </Typography>
            <Typography
              kind='body3'
              desktop='body3-d'
              mobile='body3-d'
              color='--color-foreground-secondary-bold'>
              {messages.CERTS_OF_LABEL ||
                localize(intl, getMessageKey('CERTS_OF_LABEL'))}
            </Typography>
            <Typography
              kind='body3'
              desktop='body3-d'
              mobile='body3-d'
              color='--color-foreground-secondary-bold'>
              {totalCount}
            </Typography>
            <Typography
              kind='body3'
              desktop='body3-d'
              mobile='body3-d'
              color='--color-foreground-neutral-subtle'>
              {`(${
                totalCount !== 0 ? Math.round((count * 100) / totalCount) : 0
              }%)`}
            </Typography>
            <Typography
              kind='body3-bold'
              desktop='body3-bold-d'
              mobile='body3-bold-d'
              color='--color-foreground-secondary-bold'>
              {messages.CERTS_DONE ||
                localize(intl, getMessageKey('CERTS_DONE'))}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
}

ProgressCard.propTypes = {
  cardType: PropTypes.oneOf(Object.values(CARD_TYPE)),
  action: PropTypes.shape({
    title: PropTypes.oneOf(Object.keys(DECISION_TITLES)),
    description: PropTypes.string,
  }),
  title: PropTypes.oneOf(Object.values(TYPE_TITLES_AND_ORDER)),
  count: PropTypes.number,
  totalCount: PropTypes.number,
};

ProgressCard.defaultProps = {
  cardType: null,
  action: {
    title: '',
    description: '',
  },
  title: ' ',
  count: 4,
  totalCount: 10,
};

export default ProgressCard;
